// User Service for handling user management and approvals

class UserService {
  constructor() {
    this.storageKey = 'admin_users';
    this.pendingUsersKey = 'pending_user_approvals';
  }

  // Get all users
  getUsers() {
    try {
      const users = localStorage.getItem(this.storageKey);
      return users ? JSON.parse(users) : this.getDefaultUsers();
    } catch (error) {
      console.error('Error getting users:', error);
      return this.getDefaultUsers();
    }
  }

  // Get default users for demo
  getDefaultUsers() {
    return [
      {
        id: 1,
        name: 'Admin User',
        email: 'admin@bootminds.com',
        role: 'admin',
        status: 'active',
        createdAt: '2024-01-01T00:00:00Z',
        lastLogin: new Date().toISOString()
      }
    ];
  }

  // Get pending user approvals
  getPendingApprovals() {
    try {
      const pending = localStorage.getItem(this.pendingUsersKey);
      return pending ? JSON.parse(pending) : this.getDefaultPendingUsers();
    } catch (error) {
      console.error('Error getting pending approvals:', error);
      return this.getDefaultPendingUsers();
    }
  }

  // Get default pending users for demo
  getDefaultPendingUsers() {
    return [
      {
        id: 1,
        name: 'John Smith',
        email: 'john.smith@techcorp.com',
        company: 'TechCorp Solutions',
        position: 'VP of Operations',
        reason: 'Looking to establish GCC in India for software development',
        requestDate: '2024-01-15T10:30:00Z',
        linkedIn: 'https://linkedin.com/in/johnsmith',
        companyWebsite: 'https://techcorp.com'
      },
      {
        id: 2,
        name: 'Sarah Johnson',
        email: 'sarah.j@innovationlabs.com',
        company: 'Innovation Labs Inc',
        position: 'Director of Global Operations',
        reason: 'Seeking expertise for scaling offshore development team',
        requestDate: '2024-01-14T14:20:00Z',
        linkedIn: 'https://linkedin.com/in/sarahjohnson',
        companyWebsite: 'https://innovationlabs.com'
      },
      {
        id: 3,
        name: 'Michael Chen',
        email: 'michael.chen@globalsolutions.com',
        company: 'Global Solutions Enterprise',
        position: 'CTO',
        reason: 'Planning to establish cost-effective development center',
        requestDate: '2024-01-13T09:15:00Z',
        linkedIn: 'https://linkedin.com/in/michaelchen',
        companyWebsite: 'https://globalsolutions.com'
      },
      {
        id: 4,
        name: 'Emily Rodriguez',
        email: 'emily.r@startupventures.com',
        company: 'Startup Ventures',
        position: 'Head of Engineering',
        reason: 'Need guidance on setting up remote development team in India',
        requestDate: '2024-01-12T16:45:00Z',
        linkedIn: 'https://linkedin.com/in/emilyrodriguez',
        companyWebsite: 'https://startupventures.com'
      }
    ];
  }

  // Save pending approvals
  savePendingApprovals(approvals) {
    try {
      localStorage.setItem(this.pendingUsersKey, JSON.stringify(approvals));
    } catch (error) {
      console.error('Error saving pending approvals:', error);
    }
  }

  // Save users
  saveUsers(users) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(users));
    } catch (error) {
      console.error('Error saving users:', error);
    }
  }

  // Approve a user
  approveUser(userId, adminNotes = '') {
    try {
      const pending = this.getPendingApprovals();
      const users = this.getUsers();
      
      const userIndex = pending.findIndex(user => user.id === userId);
      if (userIndex === -1) {
        throw new Error('User not found in pending approvals');
      }

      const approvedUser = pending[userIndex];
      
      // Create new user account
      const newUser = {
        id: Date.now(),
        name: approvedUser.name,
        email: approvedUser.email,
        company: approvedUser.company,
        position: approvedUser.position,
        role: 'user',
        status: 'active',
        createdAt: new Date().toISOString(),
        approvedAt: new Date().toISOString(),
        approvedBy: 'admin',
        adminNotes
      };

      // Add to users and remove from pending
      users.push(newUser);
      pending.splice(userIndex, 1);

      this.saveUsers(users);
      this.savePendingApprovals(pending);

      // Send approval email (simulated)
      this.sendApprovalEmail(newUser);

      return newUser;
    } catch (error) {
      console.error('Error approving user:', error);
      throw error;
    }
  }

  // Reject a user
  rejectUser(userId, reason = '') {
    try {
      const pending = this.getPendingApprovals();
      
      const userIndex = pending.findIndex(user => user.id === userId);
      if (userIndex === -1) {
        throw new Error('User not found in pending approvals');
      }

      const rejectedUser = pending[userIndex];
      
      // Remove from pending
      pending.splice(userIndex, 1);
      this.savePendingApprovals(pending);

      // Send rejection email (simulated)
      this.sendRejectionEmail(rejectedUser, reason);

      return true;
    } catch (error) {
      console.error('Error rejecting user:', error);
      throw error;
    }
  }

  // Simulate sending approval email
  sendApprovalEmail(user) {
    console.log(`Approval email sent to ${user.email}:`);
    console.log(`Subject: Welcome to BootMinds Elite - Account Approved`);
    console.log(`Dear ${user.name}, your account has been approved...`);
    
    // In a real application, this would integrate with an email service
    // like SendGrid, Mailgun, or AWS SES
  }

  // Simulate sending rejection email
  sendRejectionEmail(user, reason) {
    console.log(`Rejection email sent to ${user.email}:`);
    console.log(`Subject: BootMinds Elite - Account Application Update`);
    console.log(`Dear ${user.name}, unfortunately we cannot approve your account at this time.`);
    if (reason) {
      console.log(`Reason: ${reason}`);
    }
  }

  // Add new user to pending approvals (from registration form)
  addPendingUser(userData) {
    try {
      const pending = this.getPendingApprovals();
      
      const newPendingUser = {
        id: Date.now(),
        ...userData,
        requestDate: new Date().toISOString()
      };

      pending.unshift(newPendingUser);
      this.savePendingApprovals(pending);

      // Send notification to admin (simulated)
      this.notifyAdmin(newPendingUser);

      return newPendingUser;
    } catch (error) {
      console.error('Error adding pending user:', error);
      throw error;
    }
  }

  // Notify admin of new user registration
  notifyAdmin(user) {
    console.log(`New user registration: ${user.name} (${user.email}) from ${user.company}`);
    // In a real application, this would send an email or push notification to admins
  }

  // Get user statistics
  getUserStats() {
    const users = this.getUsers();
    const pending = this.getPendingApprovals();
    
    return {
      total: users.length,
      active: users.filter(u => u.status === 'active').length,
      inactive: users.filter(u => u.status === 'inactive').length,
      pending: pending.length,
      recentRegistrations: pending.filter(u => {
        const requestDate = new Date(u.requestDate);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return requestDate > weekAgo;
      }).length
    };
  }

  // Update user status
  updateUserStatus(userId, status) {
    try {
      const users = this.getUsers();
      const userIndex = users.findIndex(user => user.id === userId);
      
      if (userIndex === -1) {
        throw new Error('User not found');
      }

      users[userIndex].status = status;
      users[userIndex].updatedAt = new Date().toISOString();
      
      this.saveUsers(users);
      return users[userIndex];
    } catch (error) {
      console.error('Error updating user status:', error);
      throw error;
    }
  }

  // Delete user
  deleteUser(userId) {
    try {
      const users = this.getUsers();
      const filteredUsers = users.filter(user => user.id !== userId);
      this.saveUsers(filteredUsers);
      return true;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }
}

export default new UserService();
